import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IModificationsClient, ModificationsClient } from "app/ApiClient";
import { LoadingState } from "types";
import { IState } from "../index";
import { initialModificationsState } from "./modificationsState";
import {min as momentMin, max as momentMax, default as moment} from "moment";

const modificationsClient: IModificationsClient = new ModificationsClient(process.env.REACT_APP_API_URL);

export const getRespondentFinishDate = createAsyncThunk(
  "modifications/getRespondentFinishDate",
  async (
    {
      projectId,
      versionId,
    }: { projectId: string; versionId: string },
    { rejectWithValue },
  ) => {
    try {
      const response =  await modificationsClient.getRespondentFinishDate(projectId, versionId);      
      return response;
    } catch (error) {      
      return rejectWithValue(error);
    }
  },
);

export const createModification = createAsyncThunk(
  "modifications/createModification",
  async (
    { name, projectId, encodedVersionId }: { name: string; projectId: number; encodedVersionId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await modificationsClient.createModification({
        name,
        projectId,
        encodedVersionId,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const renameModification = createAsyncThunk(
  "modifications/renameModification",
  async ({ id, title }: { id: number; title: string }, { rejectWithValue }) => {
    try {
      const response = await modificationsClient.rename({
        id,
        name: title,
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getModifications = createAsyncThunk(
  "modifications/getModifications",
  async ({ projectId, versionId }: { projectId: number; versionId: string }, { rejectWithValue }) => {
    try {
      const response = await modificationsClient.getByProjectId(projectId, versionId);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const modificationsSlice = createSlice({
  name: "modifications",
  initialState: initialModificationsState,
  reducers: {
    resetCreatingState: (state) => {
      state.creatingState = LoadingState.idle;
      state.creatingError = null;
      state.createdModification = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createModification.pending, (state) => {
      state.creatingState = LoadingState.pending;
      state.creatingError = null;
    });
    builder.addCase(createModification.fulfilled, (state, {payload}) => {
      state.creatingState = LoadingState.succeed;
      state.creatingError = null;
      state.createdModification = payload;
    });
    builder.addCase(createModification.rejected, (state, action) => {
      state.creatingState = LoadingState.failed;
      state.creatingError = action.error.message;
    });
    builder.addCase(getModifications.pending, (state) => {
      state.modifications = [];
    });
    builder.addCase(getModifications.fulfilled, (state, action) => {
      state.modifications = action.payload;
    });
    builder.addCase(getModifications.rejected, (state) => {
      state.modifications = [];
    });
    builder.addCase(renameModification.pending, () => {});
    builder.addCase(renameModification.fulfilled, (state, action) => {
      const updated = action.payload;
      state.modifications = state.modifications.filter((mod) => mod.id !== updated.id).concat(updated);
    });
    builder.addCase(renameModification.rejected, (state) => {
      state.modifications = [];
    });
    builder.addCase(getRespondentFinishDate.fulfilled, (state, action) => {
      if(action.payload.content){
        const dates = Object.keys( action.payload.content).map((key)=> moment((action.payload.content||{})[key]));
        state.respondantDateRange[0] = momentMin(dates).toDate();
        state.respondantDateRange[1] = momentMax(dates).toDate();
      }
      else {
        state.respondantDateRange = [];
      }
    });
    builder.addCase(getRespondentFinishDate.pending, ()=>{
      //Set loading flag
    });
    builder.addCase(getRespondentFinishDate.rejected, (state)=> {
      //Clear Loading state and set error
      state.respondantDateRange=[];
    })
  },
});

export const { resetCreatingState } = modificationsSlice.actions;
export const modificationsSelector = (state: IState) => state.modifications;

export default modificationsSlice.reducer;
